import { useEffect, useMemo, useRef } from 'react';
import { io } from 'socket.io-client';
import { useDispatch } from 'react-redux';
import { WEBSOCKET_EVENTS } from '../Constants';
import api from '../redux/api';

const MAX_RECONNECTION_ATTEMPTS = 5;
const RECONNECTION_DELAY = 2000;
const MAX_CONNECTIONS = 5;

const sharedConnections = {};
const subscribedRooms = {};

const useWebSocket = (channels, updateActionMap, rooms, webSocketsRef) => {
  const dispatch = useDispatch();
  const baseUrl = import.meta.env.VITE_BASE_WS_URL;
  const reconnectTimeoutRef = useRef({});
  const connectionIds = useRef([]);
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));
  const isInitialMount = useRef(true);
  const previousRooms = useRef([]);
  const isSuspendedRef = useRef(false);

  const actionMap = useMemo(() => (
    typeof updateActionMap === 'function' ? { default: updateActionMap } : updateActionMap || {}
  ), [updateActionMap]);

  const uniqueRooms = rooms ? [...new Set(rooms.filter((r) => r))] : [];

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    const setupEventHandler = (webSocket, namespaceKey, eventName, channel) => {
      if (!sharedConnections[namespaceKey].hasSetupEvent) {
        sharedConnections[namespaceKey].hasSetupEvent = {};
      }

      if (!sharedConnections[namespaceKey].hasSetupEvent[eventName]) {
        if (!sharedConnections[namespaceKey].debounceTimers) {
          sharedConnections[namespaceKey].debounceTimers = {};
        }

        const eventHandler = (data) => {
          try {
            const payload = typeof data === 'string' ? JSON.parse(data) : data;

            if (sharedConnections[namespaceKey].debounceTimers[eventName]) {
              clearTimeout(sharedConnections[namespaceKey].debounceTimers[eventName]);
            }

            const updateAction = actionMap[eventName] || actionMap.default;
            if (!updateAction) return;

            sharedConnections[namespaceKey].debounceTimers[eventName] = setTimeout(() => {
              dispatch(updateAction({ data: payload, event: eventName, channel }));
            }, 50);
          } catch (err) {}
        };

        sharedConnections[namespaceKey].eventHandlers =
          sharedConnections[namespaceKey].eventHandlers || {};
        sharedConnections[namespaceKey].eventHandlers[eventName] = eventHandler;

        webSocket.on(eventName, eventHandler);
        sharedConnections[namespaceKey].hasSetupEvent[eventName] = true;
      }
    };

    const initializeWebSockets = () => {
      if (webSocketsRef?.current) {
        webSocketsRef.current = [];
      }
      connectionIds.current = [];

      channels.slice(0, MAX_CONNECTIONS).forEach((channel) => {
        try {
          const namespaceKey = `${baseUrl}/${channel.nameSpace}`;
          let webSocket;

          if (sharedConnections[namespaceKey]) {
            webSocket = sharedConnections[namespaceKey].socket;
            connectionIds.current.push(namespaceKey);

            if (!sharedConnections[namespaceKey].instances) {
              sharedConnections[namespaceKey].instances = [];
            }

            if (!sharedConnections[namespaceKey].instances.includes(instanceId.current)) {
              sharedConnections[namespaceKey].instances.push(instanceId.current);
            }

            [channel.responseEvent, ...(channel.additionalEvents || [])].forEach((event) => {
              setupEventHandler(webSocket, namespaceKey, event, channel);
            });
          } else {
            webSocket = io(namespaceKey, {
              reconnectionAttempts: MAX_RECONNECTION_ATTEMPTS,
              reconnectionDelay: RECONNECTION_DELAY,
              transports: ['websocket', 'polling'],
              forceNew: false,
              path: '/socket.io',
              reconnection: true,
              timeout: 10000
            });

            connectionIds.current.push(namespaceKey);
            subscribedRooms[namespaceKey] = {};

            sharedConnections[namespaceKey] = {
              socket: webSocket,
              refCount: 0,
              instances: [instanceId.current],
              reconnectState: {
                attempts: 0,
                lastError: null,
                backoffTime: RECONNECTION_DELAY
              }
            };

            webSocket.on(WEBSOCKET_EVENTS.WEBSOCKET_CONNECT_ERROR, (error) => {
              const reconnectState = sharedConnections[namespaceKey].reconnectState;
              reconnectState.attempts++;
              reconnectState.lastError = error;

              const jitter = Math.random() * 1000;
              reconnectState.backoffTime = Math.min(
                RECONNECTION_DELAY * Math.pow(1.5, reconnectState.attempts) + jitter,
                30000
              );

              if (reconnectTimeoutRef.current[namespaceKey]) {
                clearTimeout(reconnectTimeoutRef.current[namespaceKey]);
              }

              reconnectTimeoutRef.current[namespaceKey] = setTimeout(() => {
                reconnectSocket(namespaceKey);
              }, reconnectState.backoffTime);
            });

            webSocket.on(WEBSOCKET_EVENTS.WEBSOCKET_CONNECT, () => {
              if (sharedConnections[namespaceKey]) {
                sharedConnections[namespaceKey].reconnectState.attempts = 0;
                sharedConnections[namespaceKey].reconnectState.backoffTime = RECONNECTION_DELAY;
              }
              // Re-join any previously tracked rooms on successful (re)connect
              subscribedRooms[namespaceKey] = subscribedRooms[namespaceKey] || {};
              if (Array.isArray(previousRooms.current) && previousRooms.current.length > 0) {
                previousRooms.current.forEach((room) => {
                  const roomKey = room != null ? room.toString() : '';
                  if (roomKey && !subscribedRooms[namespaceKey][roomKey]) {
                    webSocket.emit('join', { room: roomKey });
                    subscribedRooms[namespaceKey][roomKey] = true;
                  }
                });
              }
            });

            webSocket.on(WEBSOCKET_EVENTS.WEBSOCKET_DISCONNECT, (reason) => {
              Object.keys(subscribedRooms[namespaceKey] || {}).forEach((room) => {
                subscribedRooms[namespaceKey][room] = false;
              });

              if (reason === WEBSOCKET_EVENTS.WEBSOCKET_IO_SERVER_DISCONNECT) {
                if (!isSuspendedRef.current) {
                  webSocket.connect();
                }
              } else if (reason === WEBSOCKET_EVENTS.WEBSOCKET_IO_CLIENT_DISCONNECT) {
                if (!isSuspendedRef.current) {
                  reconnectSocket(namespaceKey);
                }
              }
            });

            [channel.responseEvent, ...(channel.additionalEvents || [])].forEach((event) => {
              setupEventHandler(webSocket, namespaceKey, event, channel);
            });
          }

          sharedConnections[namespaceKey].refCount++;

          if (webSocketsRef?.current) {
            webSocketsRef.current.push(webSocket);
          }
        } catch (err) {}
      });
    };

    const reconnectSocket = (specificNamespace = null) => {
      const namespacesToReconnect = specificNamespace ? [specificNamespace] : connectionIds.current;

      namespacesToReconnect.forEach((namespaceKey) => {
        if (sharedConnections[namespaceKey]?.socket) {
          if (sharedConnections[namespaceKey].socket.connected) {
            sharedConnections[namespaceKey].socket.disconnect();
          }
          sharedConnections[namespaceKey].socket.connect();
        }
      });
    };

    initializeWebSockets();

    // Handle visibility change (mobile app switching, tab switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        isSuspendedRef.current = true;
        // Disconnect sockets when page is hidden to save resources
        connectionIds.current.forEach((namespaceKey) => {
          if (sharedConnections[namespaceKey]?.socket?.connected) {
            sharedConnections[namespaceKey].socket.disconnect();
          }
        });
      } else if (document.visibilityState === 'visible') {
        console.log('page becomes visible again');
        isSuspendedRef.current = false;
        // Reconnect sockets when page becomes visible again
        connectionIds.current.forEach((namespaceKey) => {
          if (sharedConnections[namespaceKey]?.socket && !sharedConnections[namespaceKey].socket.connected) {
            sharedConnections[namespaceKey].socket.connect();

            // Re-subscribe to rooms after reconnection
            if (subscribedRooms[namespaceKey]) {
              Object.keys(subscribedRooms[namespaceKey]).forEach((room) => {
                subscribedRooms[namespaceKey][room] = false;
              });
            }
          }
        });

        // Re-join rooms after reconnection
        setTimeout(() => {
          connectionIds.current.forEach((namespaceKey) => {
            const conn = sharedConnections[namespaceKey];
            if (conn?.socket?.connected && uniqueRooms.length) {
              uniqueRooms.forEach((room) => {
                conn.socket.emit('join', { room: room.toString() });
                if (subscribedRooms[namespaceKey]) {
                  subscribedRooms[namespaceKey][room] = true;
                  console.log('re-joined room', room);
                  console.log('subscribedRooms', subscribedRooms[namespaceKey]);
                }
              });
            }
          });
        }, 100);

        // Invalidate charging point queries so active views refresh their data
        dispatch(api.util.invalidateTags(['chargingPoint']));
      }
    };
    // Handle visibility change (mobile app switching, tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
      Object.keys(reconnectTimeoutRef.current).forEach((key) => {
        clearTimeout(reconnectTimeoutRef.current[key]);
      });

      connectionIds.current.forEach((namespaceKey) => {
        if (sharedConnections[namespaceKey]) {
          if (sharedConnections[namespaceKey].instances) {
            sharedConnections[namespaceKey].instances = sharedConnections[
              namespaceKey
            ].instances.filter((id) => id !== instanceId.current);
          }

          sharedConnections[namespaceKey].refCount--;

          if (sharedConnections[namespaceKey].refCount <= 0) {
            if (sharedConnections[namespaceKey].eventHandlers) {
              Object.entries(sharedConnections[namespaceKey].eventHandlers).forEach(
                ([event, handler]) => {
                  sharedConnections[namespaceKey].socket.off(event, handler);
                }
              );
            }

            if (sharedConnections[namespaceKey].debounceTimers) {
              Object.values(sharedConnections[namespaceKey].debounceTimers).forEach((timer) => {
                clearTimeout(timer);
              });
            }

            sharedConnections[namespaceKey].socket.disconnect();
            delete sharedConnections[namespaceKey];
            delete subscribedRooms[namespaceKey];
          }
        }
      });

      if (webSocketsRef?.current) {
        webSocketsRef.current = [];
      }
      connectionIds.current = [];
    };
  }, [channels, dispatch, actionMap, baseUrl]);

  useEffect(() => {
    if (!uniqueRooms.length) return;

    const hasRoomsChanged = JSON.stringify(previousRooms.current) !== JSON.stringify(uniqueRooms);
    if (!hasRoomsChanged) return;

    previousRooms.current = [...uniqueRooms];

    connectionIds.current.forEach((namespaceKey) => {
      const conn = sharedConnections[namespaceKey];
      if (conn?.socket?.connected) {
        const channel = channels.find((c) => `${baseUrl}/${c.nameSpace}` === namespaceKey);
        if (!channel) return;

        subscribedRooms[namespaceKey] = subscribedRooms[namespaceKey] || {};

        uniqueRooms.forEach((room) => {
          if (!subscribedRooms[namespaceKey][room]) {
            conn.socket.emit('join', { room: room.toString() });
            subscribedRooms[namespaceKey][room] = true;
          }
        });
      }
    });
  }, [uniqueRooms, channels, baseUrl]);

  return {
    reconnect: () => {
      connectionIds.current.forEach((namespaceKey) => {
        if (
          sharedConnections[namespaceKey]?.socket &&
          !sharedConnections[namespaceKey].socket.connected
        ) {
          sharedConnections[namespaceKey].socket.connect();
        }
      });
    },
    disconnect: () => {
      connectionIds.current.forEach((namespaceKey) => {
        if (sharedConnections[namespaceKey]?.socket) {
          sharedConnections[namespaceKey].socket.disconnect();
        }
      });
    },
    getActiveConnections: () => {
      return connectionIds.current.map((id) => ({
        url: id,
        connected: sharedConnections[id]?.socket?.connected || false,
        refCount: sharedConnections[id]?.refCount || 0,
        instances: sharedConnections[id]?.instances?.length || 0,
        subscribedRooms: subscribedRooms[id] ? Object.keys(subscribedRooms[id]).length : 0
      }));
    }
  };
};

export default useWebSocket;
